import { useMemo, useRef, useEffect } from 'react';
import { Typography, Button, Tooltip } from '@mui/material';
import { FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import { deleteLease } from '@/data';
import { useDispatch } from 'react-redux';
import { DraftLeaseCard } from '@/components/BuildingTabs/DraftLeaseCard';
import {
  exportMultipleUnitsToSpreadsheet,
  calculateLeaseMetrics,
  calculateProposedMetricsFromDraft,
  UnitPricingData,
} from '@/helpers/spreadsheetExport';
import {
  setApartmentData,
  setVisualizer,
  setHighlightedUnits,
} from '@/features/project/projectSlice';
import { GenericDistinctVisualizer } from '@/components/BuildingTabs/BuildingVisualizers';
import { OccupancyStatsHeader } from '@/components/BuildingTabs/OccupancyStatsHeader';

type DraftLeasesReportPageProps = {
  units: Unit[];
  leases?: Lease[];
  draftLeases: Lease[];
  onDeleteDraftLease?(leaseId: string): void;
  onEditDraftLease?(draftLease: Lease): void;
};

export function DraftLeasesReportPage({ units, leases, draftLeases, onDeleteDraftLease, onEditDraftLease }: DraftLeasesReportPageProps) {
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  // Create a map of unit ID to unit for quick lookup
  const unitMap = useMemo(() => {
    const map = new Map<string, Unit>();
    units.forEach((unit) => map.set(unit.id, unit));
    return map;
  }, [units]);

  // Create a map of unit ID to current lease (most recent valid lease)
  const currentLeaseMap = useMemo(() => {
    if (!leases) return new Map<string, Lease>();
    const map = new Map<string, Lease>();
    leases.forEach((lease) => {
      // Only keep the most recent lease for each unit
      const existing = map.get(lease.unitId);
      if (!existing || new Date(lease.leaseStartDate) > new Date(existing.leaseStartDate)) {
        map.set(lease.unitId, lease);
      }
    });
    return map;
  }, [leases]);

  // Set building visualization for units with draft leases
  useEffect(() => {
    if (draftLeases.length === 0) {
      dispatch(setApartmentData([]));
      dispatch(setHighlightedUnits([]));
      return;
    }

    // Color for draft leases - using a distinct color
    const DRAFT_LEASE_COLOR = '#8B5CF6'; // Purple

    const colorMap = new Map<string, string>();
    colorMap.set('draft_lease', DRAFT_LEASE_COLOR);

    const visualizer = new GenericDistinctVisualizer(colorMap);

    const apartmentData = draftLeases
      .filter((draftLease) => unitMap.has(draftLease.unitId))
      .map((draftLease) => ({
        apartmentId: draftLease.unitId,
        value: 'draft_lease',
        label: 'Draft Lease',
      }));

    dispatch(setApartmentData(apartmentData));
    dispatch(setVisualizer(visualizer));
    setTimeout(() => {
      dispatch(setHighlightedUnits(apartmentData));
    }, 100);
  }, [draftLeases, unitMap, dispatch]);

  // Handle export all draft leases to spreadsheet
  const handleExportToSpreadsheet = async () => {
    if (draftLeases.length === 0) return;

    const unitsData: UnitPricingData[] = [];

    draftLeases.forEach((draftLease) => {
      const unit = unitMap.get(draftLease.unitId);
      const currentLease = currentLeaseMap.get(draftLease.unitId);

      if (!unit || !currentLease) return;

      const currentMetrics = calculateLeaseMetrics(unit, currentLease);
      if (!currentMetrics) return;

      const proposedMetrics = calculateProposedMetricsFromDraft(unit, currentMetrics, draftLease);

      unitsData.push({
        unit,
        currentLease,
        currentMetrics,
        proposedMetrics,
      });
    });

    if (unitsData.length > 0) {
      await exportMultipleUnitsToSpreadsheet(
        unitsData,
        units,
        currentLeaseMap,
        leases
      );
    }
  };

  return (
    <div className="draft-leases-report h-[calc(100vh-112px)] w-full flex flex-col p-8 overflow-y-auto" ref={ref}>
      <OccupancyStatsHeader />
      <div className="flex justify-between items-center mb-6">
        <Typography variant="h5">
          Draft Leases
        </Typography>
        {draftLeases.length > 0 && (
          <Tooltip title="Export all draft leases to Excel">
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportToSpreadsheet}
            >
              Export to Excel
            </Button>
          </Tooltip>
        )}
      </div>

      {draftLeases.length > 0 ? (
        <div className="space-y-6">
          {draftLeases.map((draftLease) => {
            const draftUnit = unitMap.get(draftLease.unitId);
            const currentLease = currentLeaseMap.get(draftLease.unitId);
            if (!draftUnit || !currentLease) return null;

            return (
              <DraftLeaseCard
                key={draftLease.id}
                unit={draftUnit}
                currentLease={currentLease}
                draftLease={draftLease}
                onDelete={(leaseId) => {
                  deleteLease(leaseId);
                  // onDeleteDraftLease?.(leaseId);
                }}
                onEdit={(lease) => {
                  onEditDraftLease?.(lease);
                }}
              />
            );
          })}
        </div>
      ) : (
        <div className="flex items-center justify-center flex-1">
          <Typography variant="body1" color="textSecondary">
            No draft leases created yet. Create one from the Pricing Tool!
          </Typography>
        </div>
      )}
    </div>
  );
}
